import React from 'react';
import { notifications } from '../../../../engine/modules/ui/notifications.js';

const DEFAULT_NOTIFICATION_DISPLAY_TIME = 4000;
const NOTIFICATION_EXIT_TIME = 220;

const VARIANT_ICONS = {
  success: 'check_circle',
  error: 'error',
  warning: 'warning',
  info: 'info',
};

function getToastIconClass(icon) {
  return icon === '3d' ? 'material-symbols-outlined notranslate' : 'material-icons notranslate';
}

function isControlNotification(props) {
  return props.icon === '3d' && props.content === 'You Have Control';
}

function getToastClassName(props) {
  const variant = props.variant || 'info';
  const classes = ['NotificationToast', variant];

  if (props.visible) {
    classes.push('visible');
  }

  if (isControlNotification(props)) {
    classes.push('control-notification');
  }

  return classes.join(' ');
}

function ToastIcon(props) {
  return (
    <div className="ToastIcon" aria-hidden="true">
      <i className={getToastIconClass(props.icon)}>{props.icon}</i>
    </div>
  );
}

function NotificationToast(props) {
  const variant = props.variant || 'info';
  const role = variant === 'error' || variant === 'warning' ? 'alert' : 'status';

  return (
    <div
      className={getToastClassName(props)}
      role={role}
      aria-live={role === 'alert' ? 'assertive' : 'polite'}
    >
      {props.icon ? <ToastIcon icon={props.icon} /> : null}
      <div className="ToastContent">
        {props.title ? <div className="ToastTitle">{props.title}</div> : null}
        <div className="ToastMessage">{props.content}</div>
      </div>
      <button type="button" className="ToastDismiss" onClick={props.onDismiss} aria-label="Dismiss notification">
        <i className="material-icons notranslate" aria-hidden="true">close</i>
      </button>
    </div>
  );
}

class NotificationsContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      notifications: [],
      activeNotification: null,
      visible: false,
    };

    this.addNotification = this.addNotification.bind(this);
    this.showNextNotification = this.showNextNotification.bind(this);
    this.dismissActiveNotification = this.dismissActiveNotification.bind(this);
    this.clearTimers = this.clearTimers.bind(this);
  }

  componentDidMount() {
    this.unsubscribe = notifications.registerReceiver(this.addNotification);
  }

  componentWillUnmount() {
    this.clearTimers();
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  clearTimers() {
    window.clearTimeout(this.showTimer);
    window.clearTimeout(this.hideTimer);
    window.clearTimeout(this.nextTimer);
  }

  normalizeNotification(notificationData) {
    const content = notificationData.content || notificationData.message;
    const variant = notificationData.variant || notificationData.type || 'info';

    return {
      id: notificationData.id || `${Date.now()}-${Math.random()}`,
      content,
      title: notificationData.title,
      variant,
      icon: notificationData.icon || VARIANT_ICONS[variant],
      displayTime: notificationData.displayTime || DEFAULT_NOTIFICATION_DISPLAY_TIME,
    };
  }

  showNextNotification() {
    if (this.state.activeNotification || this.state.notifications.length === 0) {
      return;
    }

    this.setState((state) => ({
      activeNotification: state.notifications[0],
      notifications: state.notifications.slice(1),
      visible: false,
    }), () => {
      this.showTimer = window.setTimeout(() => {
        this.setState({ visible: true });
      }, 50);

      this.hideTimer = window.setTimeout(this.dismissActiveNotification, this.state.activeNotification.displayTime);
    });
  }

  dismissActiveNotification() {
    if (!this.state.activeNotification) {
      return;
    }

    this.clearTimers();
    this.setState({ visible: false }, () => {
      this.nextTimer = window.setTimeout(() => {
        this.setState({ activeNotification: null }, this.showNextNotification);
      }, NOTIFICATION_EXIT_TIME);
    });
  }

  addNotification(notificationData = {}) {
    if (!notificationData.content && !notificationData.message) {
      return;
    }

    const notification = this.normalizeNotification(notificationData);
    this.setState((state) => ({
      notifications: state.notifications.concat(notification),
    }), this.showNextNotification);
  }

  render() {
    const activeNotification = this.state.activeNotification;

    return (
      <div className="NotificationsContainer">
        {activeNotification ? (
          <NotificationToast
            content={activeNotification.content}
            title={activeNotification.title}
            variant={activeNotification.variant}
            icon={activeNotification.icon}
            visible={this.state.visible}
            onDismiss={this.dismissActiveNotification}
          />
        ) : null}
      </div>
    );
  }
}

export default NotificationsContainer;
